import React, { useEffect, useRef, useState } from "react";
import api, { mediaUrl, formatApiError } from "../lib/api";
import { undoableDelete } from "../lib/undo";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Upload, Trash2, ImageIcon, X } from "lucide-react";
import { usePaged, Paginator } from "../components/Paginator";

const CARD = "bg-white rounded-2xl border border-[#EBE9E4] shadow-[0_8px_30px_rgba(45,45,45,0.04)]";

export default function Gallery() {
  const { user } = useAuth();
  const isStaff = !!user?.caps?.manage_content;
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const fileRef = useRef();

  const load = async () => { const { data } = await api.get("/media", { params: { category: "gallery" } }); setItems(data); };
  useEffect(() => { load(); }, []);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f); fd.append("category", "gallery"); fd.append("title", title || f.name);
        await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${files.length} photo(s) uploaded`); setTitle(""); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const del = (id) => { setItems((l) => l.filter((x) => x.id !== id)); undoableDelete({ label: "Photo", entityType: "gallery", perform: () => api.delete(`/media/${id}`), onUndo: load }); };
  const { page, setPage, pageCount, pageItems } = usePaged(items, 9);

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="sj-page-hero flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-head text-3xl font-extrabold tracking-tight">Media Gallery</h1>
          <p style={{ color: "var(--sj-text-2)" }}>Moments from our services and events</p>
        </div>
        {isStaff && (
          <div className="flex items-center gap-2">
            <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Caption (optional)" data-testid="gallery-title-input"
              className="bg-white border border-[#EBE9E4] rounded-full px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#0B5E4E]/20" />
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={upload} data-testid="gallery-file-input" />
            <button data-testid="gallery-upload-button" disabled={uploading} onClick={() => fileRef.current?.click()}
              className="flex items-center gap-2 rounded-full px-5 py-2.5 text-white font-medium shadow-sm hover:brightness-105 disabled:opacity-60" style={{ background: "var(--sj-primary)" }}>
              <Upload className="h-5 w-5" /> {uploading ? "Uploading..." : "Upload"}
            </button>
          </div>
        )}
      </div>

      {items.length === 0 ? (
        <div className={`${CARD} p-16 text-center flex flex-col items-center gap-3`} style={{ color: "var(--sj-text-3)" }}>
          <ImageIcon className="h-12 w-12" /> No photos yet.
        </div>
      ) : (
        <div className="columns-2 md:columns-3 lg:columns-4 gap-4 space-y-4" data-testid="gallery-grid">
          {pageItems.map((m) => (
            <div key={m.id} data-testid={`gallery-item-${m.id}`} className="relative group break-inside-avoid rounded-2xl overflow-hidden border" style={{ borderColor: "var(--sj-border)" }}>
              <img src={mediaUrl(m.id)} alt={m.title} loading="lazy" onClick={() => setLightbox(m)} className="w-full cursor-pointer transition-transform duration-300 group-hover:scale-[1.03]" />
              {m.title && m.title !== m.filename && <div className="absolute bottom-0 inset-x-0 p-3 text-white text-sm font-medium bg-gradient-to-t from-black/60 to-transparent">{m.title}</div>}
              {isStaff && (
                <button data-testid={`gallery-delete-${m.id}`} onClick={() => del(m.id)} className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: "var(--sj-primary)" }}><Trash2 className="h-4 w-4" /></button>
              )}
            </div>
          ))}
        </div>
      )}
      <Paginator page={page} pageCount={pageCount} onPage={setPage} testid="gallery-paginator" />

      {lightbox && (
        <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-6" onClick={() => setLightbox(null)}>
          <button className="absolute top-5 right-5 text-white/80 hover:text-white"><X className="h-7 w-7" /></button>
          <img src={mediaUrl(lightbox.id)} alt={lightbox.title} className="max-h-[85vh] max-w-full rounded-xl" />
        </div>
      )}
    </div>
  );
}
